import React, { useEffect, useMemo, useState } from "react";
import api, { formatDate } from "../../lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { ShieldCheck, Sparkles, Search } from "lucide-react";

/**
 * Admin-only Private Beta Access control.
 *
 * Lists every coach org with current Beta Access status and lets a
 * platform_admin Grant or Revoke access. Grant defaults to +120 days
 * unless the admin picks a specific expiration date.
 */
export default function BetaAccessPanel() {
  const [orgs, setOrgs] = useState(null);
  const [filter, setFilter] = useState("");
  const [showBetaOnly, setShowBetaOnly] = useState(false);
  const [grantFor, setGrantFor] = useState(null); // org row
  const [note, setNote] = useState("");
  const [expiresAt, setExpiresAt] = useState(""); // YYYY-MM-DD
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/orgs").then((r) => setOrgs(r.data.orgs || [])).catch(() => setOrgs([]));
  };

  useEffect(() => {
    load();
  }, []);

  const rows = useMemo(() => {
    if (!orgs) return [];
    let out = orgs;
    if (showBetaOnly) out = out.filter((o) => o.beta_access);
    const q = filter.trim().toLowerCase();
    if (q) {
      out = out.filter(
        (o) =>
          (o.coach_email || "").toLowerCase().includes(q) ||
          (o.coach_name || "").toLowerCase().includes(q) ||
          (o.org_name || "").toLowerCase().includes(q),
      );
    }
    return out;
  }, [orgs, filter, showBetaOnly]);

  const openGrant = (row) => {
    setGrantFor(row);
    setNote(row.beta_access_note || "");
    setExpiresAt("");
  };

  const todayYmd = () => {
    // Local calendar date formatted as YYYY-MM-DD. Used as `min` on the
    // date picker and to defeat the iOS Safari first-tap auto-today
    // emission: if the admin never deliberately picked a date, the
    // field will either be empty or equal to today's YMD — in both
    // cases we omit `expires_at` from the payload so the backend
    // applies its 120-day default.
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const submitGrant = async () => {
    if (!grantFor) return;
    setSaving(true);
    try {
      const payload = {};
      if (note.trim()) payload.note = note.trim();
      // Only include an explicit expiration if the admin picked a real
      // future date. A blank value OR today's YMD means "use the
      // backend default" (120 days). Mobile Safari's native date picker
      // emits today's date on first tap even when the admin hasn't
      // committed a selection — treating today as "unset" guards
      // against that quirk without duplicating the 120-day math here.
      if (expiresAt && expiresAt !== todayYmd()) {
        payload.expires_at = expiresAt; // bare YMD — timezone-safe
      }
      await api.post(`/admin/orgs/${grantFor.org_id}/beta-access`, payload);
      toast.success("Private Beta Access granted");
      setGrantFor(null);
      load();
    } catch (e) {
      // Surface the backend's actual error so admins can troubleshoot
      // (invalid date, past date, missing org, etc.) without exposing
      // stack traces or secrets. Fall through to a generic friendly
      // message for network/5xx errors with no useful body.
      let msg = "Could not grant beta access";
      if (!e?.response) {
        msg = "Network error — please check your connection and try again.";
      } else {
        const detail = e.response.data?.detail;
        if (typeof detail === "string") {
          msg = detail;
        } else if (detail && typeof detail === "object" && typeof detail.message === "string") {
          msg = detail.message;
        } else if (Array.isArray(detail) && detail[0]?.msg) {
          // FastAPI 422 validation errors
          msg = `Validation error: ${detail[0].msg}`;
        } else if (e.response.status >= 500) {
          msg = `Server error (HTTP ${e.response.status}). Please try again or check backend logs.`;
        } else if (e.response.status) {
          msg = `Request failed (HTTP ${e.response.status}). Please try again.`;
        }
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (row) => {
    if (!window.confirm(`Revoke Beta Access for ${row.coach_email}?`)) return;
    try {
      await api.delete(`/admin/orgs/${row.org_id}/beta-access`);
      toast.success("Beta Access revoked");
      load();
    } catch {
      toast.error("Could not revoke beta access");
    }
  };

  if (orgs === null) {
    return (
      <div className="mt-8 rounded-xl border border-ink-100 bg-white p-6 shadow-sm">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mt-8 rounded-xl border border-ink-100 bg-white p-6 shadow-sm" data-testid="admin-beta-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="h3 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-brand" />
            Private Beta Access
          </h3>
          <p className="mt-1 text-sm text-ink-500">
            Grant selected coaches free access beyond the 14-day trial. Defaults to 120 days.
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs font-semibold text-ink-700">
          <input
            type="checkbox"
            checked={showBetaOnly}
            onChange={(e) => setShowBetaOnly(e.target.checked)}
            data-testid="admin-beta-filter-active"
          />
          Show active only
        </label>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Search className="h-4 w-4 text-ink-400" />
        <Input
          placeholder="Search by coach email, name, or org"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="h-10 max-w-md"
          data-testid="admin-beta-search"
        />
      </div>

      <div className="mt-4 divide-y divide-ink-100">
        {rows.length === 0 ? (
          <p className="py-6 text-sm text-ink-500" data-testid="admin-beta-empty">
            No coaches match.
          </p>
        ) : (
          rows.map((r) => (
            <div key={r.org_id} className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`admin-beta-row-${r.org_id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-ink-900 truncate">{r.coach_name || r.coach_email}</p>
                <p className="text-xs text-ink-500 truncate">{r.coach_email}</p>
                <p className="text-xs text-ink-400 truncate">{r.org_name} · plan: {r.plan}</p>
                {r.beta_access ? (
                  <p className="mt-1 flex items-center gap-2 text-xs text-emerald-700" data-testid={`admin-beta-status-${r.org_id}`}>
                    <Sparkles className="h-3 w-3" />
                    Active · granted {formatDate(r.beta_access_granted_at)} · expires {formatDate(r.beta_access_expires_at)}
                    {r.beta_access_note ? ` · "${r.beta_access_note}"` : ""}
                  </p>
                ) : r.beta_access_revoked_at ? (
                  <p className="mt-1 text-xs text-ink-400" data-testid={`admin-beta-status-${r.org_id}`}>
                    Revoked {formatDate(r.beta_access_revoked_at)}
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-ink-400" data-testid={`admin-beta-status-${r.org_id}`}>
                    Not granted
                  </p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {r.beta_access ? (
                  <>
                    <Button
                      variant="outline"
                      className="h-9 px-3 text-xs"
                      onClick={() => openGrant(r)}
                      data-testid={`admin-beta-extend-${r.org_id}`}
                    >
                      Extend / edit
                    </Button>
                    <Button
                      variant="outline"
                      className="h-9 px-3 text-xs text-rose-700 hover:bg-rose-50"
                      onClick={() => revoke(r)}
                      data-testid={`admin-beta-revoke-${r.org_id}`}
                    >
                      Revoke
                    </Button>
                  </>
                ) : (
                  <Button
                    className="h-9 bg-brand px-3 text-xs text-white hover:bg-brand-hover"
                    onClick={() => openGrant(r)}
                    data-testid={`admin-beta-grant-${r.org_id}`}
                  >
                    Grant beta access
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={!!grantFor} onOpenChange={(o) => { if (!o) setGrantFor(null); }}>
        <DialogContent data-testid="admin-beta-dialog">
          <DialogHeader>
            <DialogTitle>Grant Private Beta Access</DialogTitle>
            <DialogDescription>
              {grantFor ? `${grantFor.coach_email} — ${grantFor.org_name}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="beta-expires-at">Expiration date (optional — defaults to 120 days from today)</Label>
              <Input
                id="beta-expires-at"
                type="date"
                value={expiresAt}
                min={todayYmd()}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="mt-1 h-10"
                data-testid="admin-beta-expires-at"
              />
            </div>
            <div>
              <Label htmlFor="beta-note">Internal note (optional)</Label>
              <Input
                id="beta-note"
                placeholder="Cohort 1, Bob's Club, etc."
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
                className="mt-1 h-10"
                data-testid="admin-beta-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantFor(null)} disabled={saving} data-testid="admin-beta-cancel">
              Cancel
            </Button>
            <Button
              className="bg-brand text-white hover:bg-brand-hover"
              onClick={submitGrant}
              disabled={saving}
              data-testid="admin-beta-submit"
            >
              {saving ? "Saving…" : "Grant access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
